import {BillingPlanPanel} from '../../billing-plan-panel';
import {Trans} from '@ui/i18n/trans';
import {useLicenses} from './requests/use-licenses';
import {Skeleton} from '@ui/skeleton/skeleton';
import {AnimatePresence, m} from 'framer-motion';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {Button} from '@ui/buttons/button';
import {LicenseItem} from './license-item';
import {Switch} from '@ui/forms/toggle/switch';
import {useState} from 'react';

export function LicensePanel() {
  const [filterAvailable, setFilterAvailable] = useState(true);
  const query = useLicenses({filter: filterAvailable ? 'only' : '0'});

  const licenses = query.data?.licenses || [];

  if (query.isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          <p className="text-muted">Loading licenses...</p>
        </div>
      </div>
    );
  }

  return (
    <BillingPlanPanel title={<Trans message="My Licenses" />}>
      <div className="mb-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Trans message="Show only available" />
          <Switch
            checked={filterAvailable}
            onChange={e => setFilterAvailable(e.target.checked)}
          />
        </div>
      </div>
      <div>
        <AnimatePresence initial={false} mode="wait">
          <LicenseList key="licenses" licenses={licenses} hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} onLoadMore={() => query.fetchNextPage()} />
        </AnimatePresence>
      </div>
    </BillingPlanPanel>
  );
}

interface LicenseListProps {
  licenses: any[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}
function LicenseList({licenses, hasNextPage, isFetchingNextPage, onLoadMore}: LicenseListProps) {
  return (
    <m.div {...opacityAnimation}>
      <div className="flex flex-col gap-6 lg:flex-row lg:flex-wrap lg:gap-12">
        {!licenses?.length ? (
          <div className="italic text-muted w-full">
            <Trans message="No licenses yet" />
          </div>
        ) : undefined}
        {licenses?.map(license => (
          <div
            key={license.id}
            className="flex-1 min-w-0 max-w-full md:max-w-[calc(50%-1.5rem)] sm:max-w-full sm:basis-full"
          >
            <LicenseItem license={license} />
          </div>
        ))}
      </div>
      {hasNextPage && (
        <div className="flex justify-center pt-8 mt-8">
          <Button
            variant="outline"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
          >
            <Trans message="Load more" />
          </Button>
        </div>
      )}
    </m.div>
  );
}

function LoadingSkeleton() {
  return (
    <m.div {...opacityAnimation}>
      <Skeleton className="mb-14 h-80" />
      <Skeleton className="mb-14 h-80" />
      <Skeleton className="mb-14 h-80" />
    </m.div>
  );
}
